import { Autocomplete, Button, Chip, IconButton, Popper, Stack, Switch, TextField, Typography } from "@mui/material";
import { Box, styled } from "@mui/system";
import { DataGridPro, GridColDef, GridColumnVisibilityModel, GridFilterModel, GridRenderCellParams, GridSortModel, GridToolbarContainer } from "@mui/x-data-grid-pro";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState, useCallback, useRef } from "react";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { useAddScrapingMutation, useDisableActiveModeMutation, useDisableKoBiddingAutoMaxCpcMutation, useDisableKoBiddingManualMaxCpcMutation, useDisableScrapingMutation, useUpdateKoBiddingManualMutation } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useAuth } from "src/auth";
import ModalActivationsAuto from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-activations-auto";
import ModalActivationsManual from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-activations-manual";
import ConfirmDisableModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-confirm-disable";
import ModalCreateStrategy from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-create-strategy";
import ModalScraping from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-scraping";
import { formatNumber, fPercent } from "src/utils/format-number";
import { t } from "i18next";
import { formatCurrency } from "src/utils/format-currency";
import { FilterPanelCustom } from "../filter-panel-custom";
import ModalDeactivateStrategy from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-deactivate-strategy";
import { useEnforceMaxCPCMutation } from "src/context/api/liveBidding/api";
import EnforceMaxCpcModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-enforce-max-cpc";
import PresenceRateChart from "src/components/visibility/components/PresenceRateChart";
import { palette } from '../../../../src/sections/overview/dashboards/views/non-branded/figma-palette';

interface PercentageChangeProps {
    value: number | null;
    inverse?: boolean;
    rounded?: number;
    format?: string;
}

type FormatType = "percentage" | "pp";

const PercentageChange: React.FC<PercentageChangeProps & { format?: FormatType }> = ({
    value,
    inverse = false,
    rounded = 0,
    format = "percentage",
}) => {
    if (value === null || value === undefined) return null;

    const suffix = format === "pp" ? "pp" : "%";

    const isZeroAfterRound = Number(value.toFixed(rounded)) === 0;

    const isDash = value === 0 || isZeroAfterRound;

    const color =
        isDash
            ? "rgba(145, 158, 171, 1)"
            : value > 0
                ? (inverse ? "rgba(220, 24, 24, 1)" : "rgba(0, 162, 91, 1)")
                : value < 0
                    ? (inverse ? "rgba(0, 162, 91, 1)" : "rgba(220, 24, 24, 1)")
                    : "rgba(145, 158, 171, 1)";

    const style: React.CSSProperties = {
        color,
        marginLeft: "8px",
        textAlign: "right",
        fontVariantNumeric: "lining-nums tabular-nums",
        fontFamily: 'var(--font-family-Font-1, "Public Sans")',
        fontSize: "14px",
        lineHeight: "22px",
    };

    if (value > 1000) {
        return <span style={style}>{`>999${suffix}`}</span>;
    }

    if (value < -1000) {
        return <span style={style}>{`<-999${suffix}`}</span>;
    }

    const roundedValue = Number(value.toFixed(rounded));

    if (roundedValue === 0) {
        return <span style={style}>-</span>;
    }

    const sign = roundedValue > 0 ? "+" : "";

    return (
        <span style={style}>
            {sign}
            {roundedValue}
            {suffix}
        </span>
    );
};

interface Props {
    data: any[];
    rowCount: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    sortModel: GridSortModel;
    setSortModel: (model: GridSortModel) => void;
    filterModel: GridFilterModel;
    setFilterModel: (model: GridFilterModel) => void;
    localPatches: Record<string, any>;
    setLocalPatches: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    selectedDateRange?: { label: string; value: string };
    onDateRangeChange?: (newRange: { label: string; value: string }) => void;
    selectedCompareDate?: any;
    onCompareDateChange?: (newRange: { label: string; value: string }) => void;
    selectedCountry: string;
    onOpenCsvImport: () => void;

    refetch: any
}

const GreenCheckSwitch = styled(Switch)(() => ({
    width: 36,
    height: 18,
    padding: 0,
    overflow: "hidden",

    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: 0,
        top: 0,
        left: 0,
        transitionDuration: "200ms",

        "&.Mui-checked": {
            transform: "translateX(18px)",

            "& .MuiSwitch-thumb": {
                backgroundColor: "rgba(17, 141, 87, 1)",

                "&::before": {
                    content: '""',
                    width: 12,
                    height: 12,
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='20 6 9 17 4 12'/></svg>\")",
                },
            },
        },
    },

    "& .MuiSwitch-thumb": {
        width: 18,
        height: 18,
        borderRadius: "50%",
        backgroundColor: "#C83737",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        "&::before": {
            content: '""',
            width: 12,
            height: 12,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>\")",
        },
    },

    "& .MuiSwitch-track": {
        width: 36,
        height: 18,
        borderRadius: 9,
        backgroundColor: "#e9ecee",
        opacity: 1,
    },

    "& .MuiSwitch-switchBase + .MuiSwitch-track": {
        backgroundColor: "#e9ecee",
    },

    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: "#e9ecee",
    },
}));

export default function DataGridKeywordExact({
    data,
    rowCount,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    localPatches,
    setLocalPatches,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    onDateRangeChange,
    onCompareDateChange,
    selectedCompareDate,
    selectedDateRange,
    onOpenCsvImport,
    refetch
}: Props) {
    const [selectionRows, setSelectionRows]: any = useState([])
    const workspace: any = useCurrentWorkspace();
    const lang = localStorage.getItem('i18nextLng') || 'en';
    const apiRef: any = useRef();
    const toolbarRef = useRef<HTMLDivElement>(null);

    const handleCloseScraping = useCallback(() => setModalScraping({ open: false, row: null }), []);
    const handleCloseActivationManual = useCallback(() => setModalActivationManual({ open: false, isActive: false, row: null }), []);
    const handleCloseActivationAuto = useCallback(() => setModalActivationAuto({ open: false, isActive: false, row: null }), []);

    const [addScraping] = useAddScrapingMutation();
    const [disableScraping] = useDisableScrapingMutation();
    const [disableActiveMode] = useDisableActiveModeMutation();
    const [disableKoBiddingManualMaxCpc] = useDisableKoBiddingManualMaxCpcMutation();
    const [disableKoBiddingAutoMaxCpc] = useDisableKoBiddingAutoMaxCpcMutation();

    const dateRangeOptions = [
        { label: 'Last 7 Days', value: 'last7d' },
        { label: 'Last 14 Days', value: 'last14d' },
        { label: 'Last 30 Days', value: 'last30d' },
    ];

    const comparisonPeriodOptions = [
        { label: 'Prev. Period', value: 'previous_period' },
        { label: 'Last Year', value: 'last_year' },
    ];

    const [modalScraping, setModalScraping] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const [modalActivationManual, setModalActivationManual] = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [modalActivationAuto, setModalActivationAuto] = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [modalCreateStrategy, setModalCreateStrategy]: any = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [modalDeactivateStrategy, setModalDeactivateStrategy]: any = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [confirmDisable, setConfirmDisable] = useState<{ open: boolean; row: any | null, mode: string | null }>({
        open: false,
        row: null,
        mode: null
    });

    const [openEnforceModal, setOpenEnforceModal] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const [loading, setLoading] = useState(false);
    const [scrapingDetails, setScrapingDetails] = useState<any[]>([]);

    const { user } = useAuth();
    const canImport = ["admin", "brand specialist"].includes(user?.role ?? "");

    const rowsToDisplay = useMemo(() => {
        const rowsArray = Array.isArray(modalScraping.row)
            ? modalScraping.row
            : modalScraping.row ? [modalScraping.row] : [];

        let result: any[] = [];
        for (const r of rowsArray) {
            const hasActive = r.scraping_details?.some((s: any) => s.status);
            if (hasActive) {
                result.push(...r.scraping_details.map((s: any, i: number) => ({
                    ...s,
                    id: s.id ?? `${r.id}-react-${i}`,
                    status: false,
                })));
            } else {
                result.push(...r.country_name.map((country: string, i: number) => ({
                    id: `${r.id}-new-${i}`,
                    keyword: r.keyword,
                    platform_scraping: ["Google"],
                    country,
                    frequency: "2 hours",
                    device: ["desktop", "mobile"],
                    status: true,
                    country_code: r.country_code,
                })));
            }
        }
        return result;
    }, [modalScraping]);

    const mergedRows = useMemo(() => {
        return data.map((row) => {
            const patch = localPatches[row.id];

            if (!patch) return row;

            return {
                ...row,
                ...patch,
                activations_details: patch.activations_details ?? row.activations_details,
            };
        });
    }, [data, localPatches]);

    const handleToggle = useCallback((row: any, currentStatus: boolean) => {
        if (currentStatus) {
            setConfirmDisable({ open: true, row, mode: "Scraping" });
        } else {
            setModalScraping({
                open: true,
                row: [row],
            });
        }
    }, []);

    const ActivationCell = ({
        params,
        setModalActivationManual,
        setModalActivationAuto,
        setConfirmDisable,
        setModalCreateStrategy,
    }: any) => {
        const VALID_MODES = [
            "Active Mode",
            "KO Bidding Manual",
            "KO Bidding Auto",
        ];

        const details = params.row.activations_details ?? [];

        const activeDetails = details.filter(
            (d: any) =>
                d.status === true &&
                VALID_MODES.some((m) => d.mode?.startsWith(m))
        );

        const hasAnyMode = activeDetails.length > 0;

        const canCreateStrategy =
            activeDetails.filter((d: any) =>
                ["KO Bidding Manual", "KO Bidding Auto", "Active Mode"]
                    .some(m => d.mode?.startsWith(m))
            ).length <= 1;

        const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
        const [activeDetail, setActiveDetail] = useState<any>(null);

        const timeoutRef = useRef<any>(null);

        const handleEnter = (e: React.MouseEvent<HTMLElement>, detail: any) => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            setAnchorEl(e.currentTarget);
            setActiveDetail(detail);
        };

        const handleLeave = () => {
            timeoutRef.current = setTimeout(() => {
                setAnchorEl(null);
                setActiveDetail(null);
            }, 120);
        };

        const open = Boolean(anchorEl);

        const modeLabel =
            activeDetail?.mode?.startsWith("KO Bidding Auto")
                ? "Auto"
                : activeDetail?.mode === "KO Bidding Manual"
                    ? "Manual"
                    : activeDetail?.mode === "Active Mode"
                        ? "Active"
                        : activeDetail?.mode;

        return (
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    overflow: "hidden",
                    px: 1,
                }}
            >
                {hasAnyMode ? (
                    activeDetails.map((detail: any, i: number) => {
                        const isAuto = detail?.mode?.startsWith("KO Bidding Auto");
                        const isManual = detail?.mode === "KO Bidding Manual";
                        const isActive = detail?.mode === "Active Mode";

                        return (
                            <Typography
                                key={i}
                                onMouseEnter={(e) => handleEnter(e, detail)}
                                onMouseLeave={handleLeave}
                                sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    py: 0.4,
                                    px: 0.5,
                                    height: 28,
                                    display: "flex",
                                    alignItems: "center",
                                    borderRadius: "6px",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    color: isAuto
                                        ? "rgba(122, 37, 228, 1)"
                                        : isManual
                                            ? "rgba(48, 57, 176, 1)"
                                            : "rgba(103, 83, 21, 1)",
                                    background: isAuto
                                        ? "rgba(164, 111, 247, 0.19)"
                                        : isManual
                                            ? "rgba(111, 120, 247, 0.19)"
                                            : "rgba(250, 176, 0, 0.08)",
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {isActive ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="19"
                                            height="18"
                                            viewBox="0 0 19 18"
                                            fill="none"
                                        >
                                            <path
                                                d="M14.0648 15.0653C12.6411 16.0997 10.9264 16.6569 9.16658 16.6569C7.40675 16.6569 5.69209 16.0997 4.26836 15.0653M12.8197 0.833577C14.4014 1.60503 15.6983 2.85743 16.5245 4.41126C17.3507 5.9651 17.6637 7.7406 17.4188 9.4833M0.914422 9.48322C0.669501 7.74052 0.98257 5.96502 1.80876 4.41118C2.63495 2.85735 3.93184 1.60495 5.51356 0.833496M13.7499 8.32353C13.7499 10.8548 11.6979 12.9069 9.16658 12.9069C6.63527 12.9069 4.58324 10.8548 4.58324 8.32353C4.58324 5.79222 6.63527 3.7402 9.16658 3.7402C11.6979 3.7402 13.7499 5.79222 13.7499 8.32353Z"
                                                stroke="#AF8604"
                                                strokeWidth="1.66667"
                                            />
                                        </svg>
                                    ) : isManual ? (
                                        <Iconify icon="mingcute:pencil-3-line" />
                                    ) : (
                                        <Iconify icon="material-symbols:shield-outline-rounded" />
                                    )}
                                </Box>
                            </Typography>
                        );
                    })
                ) : (
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>
                        --
                    </Typography>
                )}

                <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="top"
                    style={{ zIndex: 2000 }}
                    modifiers={[
                        {
                            name: "offset",
                            options: { offset: [0, 8] },
                        },
                    ]}
                >

                    <Box
                        onMouseEnter={() => {
                            if (timeoutRef.current) clearTimeout(timeoutRef.current);
                        }}
                        onMouseLeave={handleLeave}
                        sx={{
                            display: "flex",
                            gap: .5,
                            p: 1,
                            background: "#fff",
                            borderRadius: "8px",
                            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                        }}
                    >

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.2,
                                py: 0.6,

                                borderRadius: "8px",
                                background:
                                    activeDetail?.mode?.startsWith("KO Bidding Auto")
                                        ? "rgba(164, 111, 247, 0.19)"
                                        : activeDetail?.mode === "KO Bidding Manual"
                                            ? "rgba(111, 120, 247, 0.19)"
                                            : "rgba(250, 176, 0, 0.08)",
                                color:
                                    activeDetail?.mode?.startsWith("KO Bidding Auto")
                                        ? "rgba(122, 37, 228, 1)"
                                        : activeDetail?.mode === "KO Bidding Manual"
                                            ? "rgba(48, 57, 176, 1)"
                                            : "rgba(103, 83, 21, 1)",
                            }}
                        >
                            {activeDetail?.mode === "KO Bidding Manual" ? (
                                <Iconify icon="mingcute:pencil-3-line" width={16} />
                            ) : activeDetail?.mode?.startsWith("KO Bidding Auto") ? (
                                <Iconify icon="material-symbols:shield-outline-rounded" width={16} />
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 19 18"
                                    fill="none"
                                >
                                    <path
                                        d="M14.0648 15.0653C12.6411 16.0997 10.9264 16.6569 9.16658 16.6569C7.40675 16.6569 5.69209 16.0997 4.26836 15.0653M12.8197 0.833577C14.4014 1.60503 15.6983 2.85743 16.5245 4.41126C17.3507 5.9651 17.6637 7.7406 17.4188 9.4833M0.914422 9.48322C0.669501 7.74052 0.98257 5.96502 1.80876 4.41118C2.63495 2.85735 3.93184 1.60495 5.51356 0.833496M13.7499 8.32353C13.7499 10.8548 11.6979 12.9069 9.16658 12.9069C6.63527 12.9069 4.58324 10.8548 4.58324 8.32353C4.58324 5.79222 6.63527 3.7402 9.16658 3.7402C11.6979 3.7402 13.7499 5.79222 13.7499 8.32353Z"
                                        stroke="#AF8604"
                                        strokeWidth="1.66667"
                                    />
                                </svg>
                            )}

                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                {modeLabel}
                            </Typography>
                        </Box>

                        {activeDetail?.mode !== "Active Mode" && (
                            <IconButton
                                size="small"
                                onMouseDown={(e) => {
                                    e.stopPropagation();

                                    if (activeDetail?.mode === "KO Bidding Manual") {
                                        setModalActivationManual({
                                            open: true,
                                            isActive: true,
                                            row: params.row,
                                        });
                                    } else {
                                        setModalActivationAuto({
                                            open: true,
                                            isActive: true,
                                            row: params.row,
                                        });
                                    }

                                    setAnchorEl(null);
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                    <path d="M9.93331 0.600098L12.2666 2.93343M0.599976 12.2668L1.34454 9.53668C1.39312 9.35857 1.41741 9.26951 1.45469 9.18646C1.4878 9.11273 1.52848 9.04263 1.57608 8.9773C1.62968 8.90372 1.69496 8.83845 1.8255 8.7079L7.85333 2.68008C7.96883 2.56458 8.02658 2.50682 8.09318 2.48519C8.15176 2.46615 8.21486 2.46615 8.27344 2.48519C8.34004 2.50682 8.39779 2.56458 8.51329 2.68008L10.1867 4.35345C10.3022 4.46895 10.3599 4.52671 10.3816 4.5933C10.4006 4.65188 10.4006 4.71498 10.3816 4.77356C10.3599 4.84016 10.3022 4.89791 10.1867 5.01341L4.15884 11.0412C4.02829 11.1718 3.96302 11.2371 3.88944 11.2907C3.82411 11.3383 3.75401 11.3789 3.68028 11.412C3.59723 11.4493 3.50817 11.4736 3.33006 11.5222L0.599976 12.2668Z" stroke="#5E6D7B" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </IconButton>
                        )}

                        <IconButton
                            size="small"
                            onMouseDown={(e) => {
                                e.stopPropagation();

                                setConfirmDisable({
                                    open: true,
                                    row: params.row,
                                    mode: activeDetail?.mode,
                                });

                                setAnchorEl(null);
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M5.25 1.75H8.75M1.75 3.5H12.25M11.0833 3.5L10.6742 9.63625C10.6129 10.5569 10.5822 11.0172 10.3833 11.3663C10.2083 11.6735 9.94422 11.9206 9.62597 12.0748C9.26448 12.25 8.80314 12.25 7.88045 12.25H6.11955C5.19686 12.25 4.73552 12.25 4.37403 12.0748C4.05577 11.9206 3.79172 11.6735 3.61666 11.3663C3.41781 11.0172 3.38713 10.5569 3.32575 9.63625L2.91667 3.5" stroke="#5E6D7B" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </IconButton>
                    </Box>
                </Popper>

                {canCreateStrategy && (
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            setModalCreateStrategy({
                                open: true,
                                row: [params.row],
                            });
                        }}
                        sx={{
                            position: "absolute",
                            right: 6,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 22,
                            height: 22,
                            borderRadius: "6px",
                            backgroundColor: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            opacity: 0,
                            transition: "all 0.2s ease",
                            ".MuiDataGrid-cell:hover &": {
                                opacity: 1,
                            },
                        }}
                    >
                        <Typography sx={{ fontSize: 20, lineHeight: 1 }}>
                            +
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    const columns: GridColDef[] = useMemo(() => [
        {
            field: "keyword",
            headerName: t("dashboard.global.keyword"),
            disableColumnMenu: true,
            minWidth: 225,
            headerClassName: "keyword-header",
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        {t("dashboard.global.keyword")}
                    </Typography>

                    <CustomTooltip title={"Ad clicks"}
                        placement="top"
                        arrow>
                        <Iconify
                            icon="proicons:cursor-click"
                            width={20}
                            sx={{ cursor: 'help' }}
                            height={20}
                        />
                    </CustomTooltip>
                </Box>
            ),
            renderCell: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        height: "60px",
                        padding: "0px 12px 0px 8px",
                        gap: 3
                    }}
                >
                    <CustomTooltip title={params.row.keyword} arrow>
                        <Typography
                            fontSize={14}
                            color="rgba(51, 60, 71, 1)"
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                cursor: 'help'
                            }}
                        >
                            [{params.row.keyword}]
                        </Typography>
                    </CustomTooltip>

                    <Typography
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                        sx={{
                            marginLeft: "auto",
                            textAlign: "right",
                        }}
                    >
                        {formatNumber(params.row.clicks)}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "keyword_id",
            valueGetter: (params) => {
                return String(params).replace(/\s+/g, '');
            },
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Keyword ID
                    </Typography>
                </Box>
            ),
            headerName: "Keyword ID",
            minWidth: 200,
            type: "number",
            sortable: false,
            disableColumnMenu: true,
        },
        {
            field: "platform",
            headerName: "Platform",
            minWidth: 140,
            sortable: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Platform
                    </Typography>
                </Box>
            ),
            type: "singleSelect",
            valueOptions: [
                { value: "GOOGLE", label: "Google Ads" },
                { value: "BING", label: "Bing Ads" },
            ],

            valueGetter: (params: any) => (params || "").toUpperCase(),

            renderCell: (params) => {
                const map: any = {
                    GOOGLE: "Google Ads",
                    BING: "Bing Ads",
                };

                return map[params.value] || params.value;
            },
        },
        // Campaigns
        {
            field: "campaign_names",
            sortable: true,
            disableColumnMenu: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Campaign
                    </Typography>
                </Box>
            ),
            headerName: "Campaign",
            minWidth: 160,
            renderCell: ({ value }) => {
                return (
                    <CustomTooltip
                        title={value?.join(", ")}
                        placement="top"
                        arrow
                        slotProps={{
                            tooltip: {
                                sx: {
                                    px: "12px",
                                    py: "8px",
                                    borderRadius: "8px",
                                    backgroundColor: "#FCFCFD",
                                    boxShadow: "1px 3px 8px 0 rgba(0, 0, 0, 0.08)",
                                },
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                height: "60px",
                                px: "12px",
                                minWidth: 0,
                                overflow: "hidden",
                            }}
                        >
                            <Typography
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontSize: '14px',
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    color: "rgba(51, 60, 71, 1)",
                                    cursor: 'help'
                                }}
                            >
                                {(value || []).join(", ")}
                            </Typography>
                        </Box>
                    </CustomTooltip>
                );
            }
        },
        {
            field: "campaign_ids",
            type: 'number',
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Campaign ID
                    </Typography>
                </Box>
            ),
            disableColumnMenu: true,
            headerName: "Campaign ID",
            flex: 1,
            sortable: false
        },
        {
            field: "ad_group_names",
            disableColumnMenu: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Ad Group
                    </Typography>
                </Box>
            ),
            headerName: "Ad Group Name",
            minWidth: 200,
            sortable: true
        },
        {
            field: "ad_group_ids",
            type: 'number',
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Ad Group ID
                    </Typography>
                </Box>
            ),
            disableColumnMenu: true,
            headerName: "Ad Group ID",
            flex: 1,
            sortable: false
        },
        // Scraping Status
        {
            field: "scraping_status",
            headerName: "Scraping Status",
            sortable: false,
            disableColumnMenu: true,
            filterable: true,
            width: 85,
            type: "boolean",
            minWidth: 85,
            maxWidth: 85,
            renderHeader: () => (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                    }}
                >
                    <Typography fontWeight={600} fontSize={14} color="rgba(99,115,129,1)">
                        Scraping
                    </Typography>
                    <Typography fontWeight={600} fontSize={14} color="rgba(99,115,129,1)">
                        Status
                    </Typography>
                </Box>
            ),

            renderCell: ({ row }) => {
                const activeDetail = row.scraping_details?.find((s: any) => s.status === true);
                const freq = activeDetail?.frequency;

                const content = (
                    <Box
                        sx={{
                            width: "100%",
                            height: "60px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "0px 12px",
                        }}
                    >
                        <GreenCheckSwitch
                            checked={!!activeDetail}
                            onChange={() => handleToggle(row, !!activeDetail)}
                        />
                    </Box>
                );

                if (!activeDetail) return content;

                return (
                    <CustomTooltip
                        title={`Frequency: ${freq || "N/A"}`}
                        placement="top"
                        arrow
                        slotProps={{
                            tooltip: {
                                sx: {
                                    px: "12px",
                                    py: "8px",
                                    borderRadius: "8px",
                                    backgroundColor: "#FCFCFD",
                                    boxShadow: "0px 3px 8px rgba(0,0,0,0.08)",
                                },
                            },
                        }}
                    >
                        {content}
                    </CustomTooltip>
                );
            },
        },
        {
            field: "mode",
            headerName: "Mode",
            disableColumnMenu: true,
            filterable: true,
            sortable: false,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                        flexDirection: "column"
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Mode
                    </Typography>
                </Box>
            ),
            minWidth: 100,
            editable: false,
            filterOperators: [
                {
                    label: "is any of",
                    value: "isAnyOf",
                    getApplyFilterFn: () => null,

                    InputComponent: (props) => {
                        const { item, applyValue } = props;

                        return (
                            <Autocomplete
                                sx={{ mt: 2.5 }}
                                multiple
                                options={[
                                    "Active Mode",
                                    "KO Bidding Manual",
                                    "KO Bidding Auto",
                                ]}
                                value={item.value || []}
                                onChange={(event, newValue) => {
                                    applyValue({
                                        ...item,
                                        value: newValue,
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        placeholder="Select modes"
                                    />
                                )}
                                size="small"
                            />
                        );
                    },
                },
            ],
            renderCell: (params) => (
                <ActivationCell
                    params={params}
                    setModalActivationManual={setModalActivationManual}
                    setModalActivationAuto={setModalActivationAuto}
                    setConfirmDisable={setConfirmDisable}
                    setModalCreateStrategy={setModalCreateStrategy}
                />
            )
        },
        {
            disableColumnMenu: true,
            field: "max_cpc",
            filterable: false,
            type: "string",
            sortable: false,
            headerAlign: 'left',
            headerName: "Max CPC",

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    fontSize={14}
                    color="rgba(99, 115, 129, 1)"
                    sx={{
                        whiteSpace: "normal",
                        lineHeight: 1.2,
                    }}
                >
                    Set Max CPC
                </Typography>
            ),

            minWidth: 90,
            editable: true,
            renderEditCell: (params) => {
                return (
                    <Box
                        display="flex"
                        width="100%"
                        height="100%"
                        alignItems="center"
                        justifyContent="flex-end"
                        px={1}
                    >
                        <TextField
                            autoFocus
                            value={params.value ?? ""}
                            onChange={(event) => {
                                let value = event.target.value;

                                value = value.replace(",", ".");

                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: params.field,
                                    value,
                                });
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            inputProps={{
                                style: { textAlign: "right" },
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    textAlign: "right",
                                    "& fieldset": {
                                        borderRadius: "6px",
                                    },
                                    "&:hover fieldset": {
                                        border: "1px solid var(--color-azure-58, #338AF3)",
                                        background: "rgba(255, 255, 255, 0.01)",
                                        boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                    },
                                    "&.Mui-focused fieldset": {
                                        border: "1px solid var(--color-azure-58, #338AF3)",
                                        boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                    },
                                },
                                "& input": {
                                    textAlign: "right",
                                    cursor: "pointer",
                                },
                            }}
                        />
                    </Box>
                );
            },

            renderCell: (params: any) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) => d.status === true && d.mode === "KO Bidding Manual"
                );

                const koAuto = activations.find(
                    (d: any) => d.status === true && d.mode?.startsWith("KO Bidding Auto")
                );

                const value =
                    params.row.max_cpc !== undefined
                        ? params.row.max_cpc
                        : koManual?.max_cpc;

                const isAuto = !koManual && Boolean(koAuto);
                const isActive = Boolean(koManual);

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            width: "100%",
                            height: "100%",
                            boxSizing: "border-box",
                            minWidth: 0,
                        }}
                    >
                        {isActive ? (
                            isAuto ? (
                                <span style={{ opacity: 0.5, width: 52, textAlign: "right", display: "inline-block" }}>
                                    {params?.row?.max_cpc}
                                </span>
                            ) : (
                                <Box
                                    onClick={() => {
                                        params.api.startCellEditMode({
                                            id: params.id,
                                            field: params.field,
                                        });
                                    }}
                                    sx={{
                                        width: 80,
                                        height: 36,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        px: "10px",
                                        borderRadius: "6px",
                                        border: "1px solid rgba(145, 158, 171, 0.20)",
                                        background: "rgba(255, 255, 255, 0.01)",
                                        fontSize: "0.875rem",
                                        lineHeight: 1,
                                        whiteSpace: "nowrap",
                                        boxSizing: "border-box",
                                        cursor: "pointer",
                                        transition: "all 0.15s ease",
                                        "&:hover": {
                                            border: "1px solid var(--color-azure-58, #338AF3)",
                                            boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                        },
                                    }}
                                >
                                    {formatCurrency(value, workspace?.currency, lang, true, 2) ?? ""}
                                </Box>
                            )
                        ) : (
                            <Typography
                                sx={{
                                    width: 52,
                                    textAlign: "right",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    lineHeight: "14px",
                                    color: "#adb1b5",
                                    pr: 1.2,
                                }}
                            >
                                {formatCurrency(params.row.max_cpc_3, workspace?.currency, lang, true, 2) ?? ""}
                            </Typography>
                        )}
                    </Box>
                );
            },
        },
        {
            field: "max_cpc_2",
            sortable: true,
            headerAlign: "left",
            disableColumnMenu: true,
            filterable: true,
            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    fontSize={14}
                    color="rgba(99, 115, 129, 1)"
                    sx={{
                        whiteSpace: "normal",
                        lineHeight: 1.2,
                    }}
                >
                    Max CPC Evol
                </Typography>
            ),
            headerName: "Max CPC Evol",
            renderCell: ({ row }) => {
                const currentMaxCpc = row.max_cpc_2 ?? 0;
                const compareMaxCpc = row?.max_cpc_compare ?? 0;

                let percentageChange = null;

                if (compareMaxCpc > 0) {
                    percentageChange =
                        ((currentMaxCpc - compareMaxCpc) / compareMaxCpc) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "12px",
                            boxSizing: "border-box",
                            gap: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#333C47",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatCurrency(currentMaxCpc, workspace?.currency, lang, true, 2) ?? ""}
                        </Typography>

                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                            }}
                        >
                            <PercentageChange value={percentageChange} inverse />
                        </Box>
                    </Box>
                );
            },
            minWidth: 144,
            type: "number",
        },
        {
            field: "cost",
            disableColumnMenu: true,
            sortable: true,
            headerAlign: "left",
            filterable: true,

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    color="rgba(99, 115, 129, 1)"
                    fontSize={14}
                >
                    Ad Cost
                </Typography>
            ),

            headerName: "Ad Cost",

            renderCell: ({ row }) => {
                const currentCost = row.cost ?? 0;
                const compareCost = row?.cost_compare ?? 0;

                let percentageChange = null;

                if (compareCost > 0) {
                    percentageChange =
                        ((currentCost - compareCost) / compareCost) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "12px",
                            boxSizing: "border-box",
                            gap: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#333C47",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatCurrency(
                                currentCost,
                                workspace?.currency,
                                lang,
                                true,
                                0
                            )}
                        </Typography>

                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            <PercentageChange value={percentageChange} inverse={true} />
                        </Box>
                    </Box>
                );
            },
            minWidth: 144,
            type: "number",
        },
        // AVG. CPC
        {
            field: "avg_cpc",
            disableColumnMenu: true,
            sortable: true,
            headerAlign: "left",
            filterable: true,

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    color="rgba(99, 115, 129, 1)"
                    fontSize={14}
                >
                    Avg. CPC
                </Typography>
            ),

            headerName: "Avg. CPC",

            renderCell: ({ row }) => {
                const currentCpc = row.avg_cpc ?? 0;
                const compareCpc = row?.avg_cpc_compare ?? 0;

                let percentageChange = null;

                if (compareCpc > 0) {
                    percentageChange =
                        ((currentCpc - compareCpc) / compareCpc) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "12px",
                            boxSizing: "border-box",
                            gap: "8px",
                            width: "100%",
                        }}
                    >
                        <Typography
                            sx={{
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#333C47",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatCurrency(
                                currentCpc,
                                workspace?.currency,
                                lang,
                                true,
                                2
                            )}
                        </Typography>

                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            <PercentageChange value={percentageChange} inverse />
                        </Box>
                    </Box>
                );
            },

            minWidth: 140,
            type: "number",
        },
        // BLENDED CTR
        {
            field: "blended_ctr",
            sortable: true,
            disableColumnMenu: true,
            filterable: true,
            headerAlign: "left",
            renderHeader: (params) => (

                <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                    Blended CTR
                </Typography>
            ),
            headerName: "Blended CTR",
            renderCell: (params: GridRenderCellParams) => {
                const rawValue = params?.row?.blended_ctr || 0;
                const rawCompare = params?.row?.blended_ctr_compare || 0;

                const currentCTR = rawValue * 100;
                const compareCTR = rawCompare * 100;

                const color = palette.visibility_range.find(({ min, max }) => {
                    return currentCTR >= min && currentCTR <= max;
                })?.color;

                let percentageChange: number | null = null;

                if (compareCTR > 0) {
                    percentageChange = ((currentCTR - compareCTR) / compareCTR) * 100;
                }

                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                            width: "100%",
                            height: "60px",

                            px: 0.5,
                            gap: 1,
                            boxSizing: "border-box",

                        }}
                    >
                        <Typography
                            sx={{
                                width: 30,
                                flexShrink: 0,
                                color: "#333C47",
                                fontVariantNumeric: "tabular-nums",
                                fontSize: 14,
                            }}
                        >
                            {currentCTR.toFixed(0)}%
                        </Typography>

                        <Box
                            sx={{
                                height: 8,
                                backgroundColor: "transparent", // 👈 fond visible (track)
                                borderRadius: "6px",
                                overflow: "visible", // 👈 IMPORTANT pour ne pas couper les bordures
                                mx: 0.5,
                                minWidth: 10,
                                position: "relative",
                                width: "100%"
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${Math.min(Math.max(currentCTR, 0), 100)}%`,
                                    height: "100%",
                                    backgroundColor: color,

                                    borderLeft: "2px solid rgba(0,0,0,0.15)",
                                    borderRight: "2px solid rgba(0,0,0,0.15)",
                                    boxSizing: "border-box",

                                    borderRadius: "6px",
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                flexShrink: 0,
                                textAlign: "right",
                            }}
                        >
                            <PercentageChange
                                format="pp"
                                value={percentageChange}
                                inverse={false}
                            />
                        </Box>
                    </Stack>
                );
            },
            minWidth: 212,
            type: "number"
        },
        {
            field: "blended_cpc",
            sortable: true,
            disableColumnMenu: true,
            headerAlign: "left",
            filterable: true,

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    color="rgba(99, 115, 129, 1)"
                    fontSize={14}
                >
                    Blended CPC
                </Typography>
            ),

            headerName: "Blended CPC",

            renderCell: ({ row }) => {
                const currentCpc = row.blended_cpc ?? 0;
                const compareCpc = row?.blended_cpc_compare ?? 0;

                let percentageChange = null;

                if (compareCpc > 0) {
                    percentageChange =
                        ((currentCpc - compareCpc) / compareCpc) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "12px",
                            boxSizing: "border-box",
                            gap: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#333C47",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatCurrency(
                                currentCpc,
                                workspace?.currency,
                                lang,
                                true,
                                2
                            )}
                        </Typography>

                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            <PercentageChange value={percentageChange} inverse />
                        </Box>
                    </Box>
                );
            },

            minWidth: 144,

            type: "number",
        },
        {
            field: 'visibility_rate',
            disableColumnMenu: true,
            headerAlign: "left",
            headerName: 'Visibility rate',
            type: 'number',
            flex: 1,
            minWidth: 225,
            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    color="rgba(99, 115, 129, 1)"
                    fontSize={14}
                >
                    Visibility Rate
                </Typography>
            ),

            valueGetter: (params: any) => params ?? 0,

            renderCell: (params: GridRenderCellParams) => {
                const data = {
                    organicOnly: params?.row?.organic_rate || 0,
                    soloBidder: Number(params?.row?.solo_bidder) || 0,
                    topAdPosition: Number(params?.row?.top_ad) || 0,
                    competitorOnly: Number(params?.row?.competitor_only) || 0,
                };

                const currentVisibilityRate = params?.row?.visibility_rate || 0;
                const compareVisibilityRate = params?.row?.visibility_rate_compare || 0;

                const delta = compareVisibilityRate - currentVisibilityRate;

                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                            width: "100%",
                            height: 60,
                            gap: 1,
                            overflow: "hidden",
                            minWidth: 0,
                            boxSizing: "border-box",
                        }}
                    >

                        {/* LEFT VALUE */}
                        <Box
                            sx={{
                                width: 50,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="body2">
                                {fPercent(Math.round(currentVisibilityRate))}
                            </Typography>
                        </Box>

                        {/* BAR / CHART */}
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                height: 24,
                                display: "flex",
                                alignItems: "center",
                                overflow: "hidden",
                            }}
                        >
                            <PresenceRateChart
                                round={1}
                                data={data}
                                height={40}
                                totalVisibilityRate={currentVisibilityRate}
                                colors={[
                                    "#18A155",
                                    "#C09910",
                                    "#F9DE81",
                                    "#e9ecee"
                                ]}
                            />
                        </Box>

                        {/* RIGHT DELTA */}
                        <Box
                            sx={{
                                minWidth: 50,
                                flexShrink: 0,
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                            }}
                        >
                            <PercentageChange
                                value={delta}
                                format="pp"
                            />
                        </Box>

                    </Stack>
                );
            },
        }
    ], [handleToggle, workspace?.currency, lang, selectedCompareDate]);

    const handleDisableActiveMode = useCallback(
        async (detail: any) => {
            if (!workspace) return;

            try {
                await disableActiveMode({
                    id: detail.id,
                    workspace_id: workspace.id,
                }).unwrap();

                enqueueSnackbar(`Active Mode disabled successfully`, { variant: "success" });

                const rowToPatch = confirmDisable.row;

                if (!rowToPatch) return;

                setLocalPatches(prev => ({
                    ...prev,
                    [rowToPatch.id]: {
                        ...rowToPatch,
                        activations_details: rowToPatch.activations_details.filter(
                            (d: any) => d.mode !== "Active Mode"
                        )
                    }
                }));

            } catch (err: any) {
                console.error(err);
                enqueueSnackbar(`Failed to disable Active Mode`, { variant: "error" });
            }
        },
        [disableActiveMode, workspace, confirmDisable.row, setLocalPatches]
    );

    const handleConfirmScraping = useCallback(
        async (rowsToDelete: Set<any>) => {
            const rows = modalScraping.row;
            if (!rows || !workspace) return;

            setLoading(true);

            try {
                // 🔥 On récupère les détails à envoyer
                const details = scrapingDetails.length > 0 ? scrapingDetails : rowsToDisplay;

                // 🔥 On ne garde que les lignes NON supprimées
                const activeRows = details.filter((row) => !rowsToDelete.has(row.id));

                const payloadArray: any = activeRows.map((detail, index) => {
                    const originalRow = rows[index] ?? rows[0];

                    return {
                        keyword: detail.keyword ?? originalRow.keyword,
                        country: Array.isArray(detail.country) ? detail.country : [detail.country],
                        country_code: [originalRow.country_code[index]],
                        home_language: originalRow.home_language[0],
                        frequency: detail.frequency ?? "60 min",
                        platform_scraping: Array.isArray(detail.platform_scraping)
                            ? detail.platform_scraping
                            : [detail.platform_scraping ?? "Google"],
                        device: detail.device ?? ["desktop", "mobile"],
                        workspaceId: workspace.id,
                        client: workspace.bqTable,
                        mode: "Scraping",
                    };
                });

                const result = await addScraping(payloadArray).unwrap();

                enqueueSnackbar(`Scraping added successfully.`, { variant: "success" });

                // 🔥 Mise à jour locale
                rows.forEach((r: any) => {
                    setLocalPatches((prev) => ({
                        ...prev,
                        [r.id]: {
                            ...prev[r.id],
                            scraping_details: result.map((newScraping: any) => ({
                                ...newScraping,
                                status: true,
                            })),
                        },
                    }));
                });

                handleCloseScraping();
            } catch (err) {
                console.error(err);
                enqueueSnackbar(`Failed to add scraping`, { variant: "error" });
            } finally {
                setLoading(false);
            }
        },
        [
            modalScraping.row,
            workspace,
            scrapingDetails,
            rowsToDisplay,
            addScraping,
            setLocalPatches,
            handleCloseScraping
        ]
    );

    const handleDisableScraping = useCallback(
        async (detail: any) => {
            if (!workspace) return;

            try {
                await disableScraping({ id: detail.id }).unwrap();
                enqueueSnackbar(`Scraping disabled successfully`, { variant: "success" });

                const rowToPatch = confirmDisable.row;

                if (!rowToPatch) return;

                setLocalPatches(prev => ({
                    ...prev,
                    [rowToPatch.id]: {
                        ...rowToPatch,
                        scraping_details: []
                    }
                }));

            } catch (err: any) {
                console.error(err);
                enqueueSnackbar(`Failed to disable Scraping`, { variant: "error" });
            }
        },
        [disableScraping, workspace, confirmDisable.row, setLocalPatches]
    );

    const handleConfirmDisable = useCallback(
        async (row: any) => {
            if (!row) return;

            const modeToDisable = confirmDisable.mode;

            try {
                if (modeToDisable === "Active Mode") {
                    const detail = row.activations_details?.find((d: any) => d.mode === "Active Mode");
                    await handleDisableActiveMode(detail);
                }

                else if (modeToDisable === "Scraping") {
                    const detail = row.scraping_details?.[0];
                    await handleDisableScraping(detail);
                }

                else if (modeToDisable === "KO Bidding Manual") {
                    const detail = row.activations_details?.find(
                        (d: any) => d.mode === "KO Bidding Manual"
                    );

                    if (detail?.id) {
                        await disableKoBiddingManualMaxCpc({ id: detail.id }).unwrap();

                        enqueueSnackbar(`KO Bidding Manual disabled successfully`, {
                            variant: "success",
                        });

                        setLocalPatches(prev => {
                            const existing = prev[row.id] ?? row;

                            const nextActivations = existing.activations_details.filter(
                                (d: any) => d.mode !== "KO Bidding Manual"
                            );

                            const koManual = nextActivations.find(
                                (a: any) =>
                                    a.status &&
                                    a.mode === "KO Bidding Manual"
                            );

                            return {
                                ...prev,
                                [row.id]: {
                                    ...existing,
                                    activations_details: nextActivations,
                                    max_cpc: koManual?.max_cpc ?? null,
                                    mode: nextActivations?.[0]?.mode ?? null,
                                }
                            };
                        });
                    }
                }

                else if (modeToDisable?.startsWith("KO Bidding Auto")) {
                    const detail = row.activations_details?.find(
                        (d: any) => d.mode?.startsWith("KO Bidding Auto")
                    );

                    if (detail?.id) {
                        await disableKoBiddingAutoMaxCpc({ id: detail.id }).unwrap();

                        enqueueSnackbar(`KO Bidding Auto disabled successfully`, { variant: "success" });

                        // 🔥 Patch local instantané
                        setLocalPatches(prev => ({
                            ...prev,
                            [row.id]: {
                                ...row,
                                activations_details: row.activations_details.filter(
                                    (d: any) => !d.mode?.startsWith("KO Bidding Auto")
                                ),
                                max_cpc: null,
                            }
                        }));
                    }
                }

                refetch()
                setConfirmDisable({ open: false, row: null, mode: null });

            } catch (err) {
                enqueueSnackbar(`Failed to disable ${modeToDisable}`, { variant: "error" });
            }
        },
        [
            confirmDisable,
            handleDisableActiveMode,
            handleDisableScraping,
            disableKoBiddingManualMaxCpc,
            disableKoBiddingAutoMaxCpc,
            setLocalPatches
        ]
    );

    const [enforceMaxCPC] = useEnforceMaxCPCMutation();
    const [updateMaxCpc] = useUpdateKoBiddingManualMutation();

    const handleUpdateMaxCPC = async (modifiedRows: any[]) => {
        try {
            const payload = modifiedRows.map((row) => ({
                id: row.id,
                workspaceId: workspace?.id,
                max_cpc: Number(row.max_cpc),
            }));

            await updateMaxCpc(payload).unwrap();

            setLocalPatches((prev) => {
                const next = { ...prev };

                for (const row of modifiedRows) {
                    next[row.rowId] = {
                        ...next[row.rowId],
                        max_cpc: row.max_cpc,
                        isEdited: false,
                        isInvalid: false,
                    };
                }

                return next;
            });

            setModifiedRows([]);

            const count = modifiedRows.length;

            enqueueSnackbar(
                `Update${count > 1 ? "s" : ""} applied successfully`,
                { variant: "success" }
            );

        } catch (error) {
            console.error("Update failed:", error);

            setLocalPatches({});
            setModifiedRows([]);
        }
    };

    const handleFinish = async (rows: any[], setResponse: (res: any[]) => void) => {

        const payload = {
            keywords: rows.map((row) => ({
                customer_id: String(row.account_id),
                account_id: String(row.account_id),
                ad_group_id: String(row.ad_group_ids[0]),
                platform: row?.platform === "GOOGLE" ? "Google Ads"
                    : row?.platform === "BING" ? "Bing Ads"
                        : row?.platform,

                keyword_id: String(row.keyword_id),
                keyword: row.keyword,
                match_type: row.match_type,
                campaign_id: String(row.campaign_ids[0]),
                max_cpc: row.max_cpc_to_enforce,
                name: workspace?.bqTable,
                country_code: row.country_code,
                message: 'Enforce max CPC from front-end'
            }))
        };


        try {
            const response = await enforceMaxCPC(payload).unwrap();
            setResponse(response);
        } catch (error) {
            console.error('Enforce error', error);
        }
    };

    const CustomToolbar = ({ selectionRows = [] }: any) => {
        return (
            <GridToolbarContainer
                ref={toolbarRef}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: 40,
                    minHeight: 40,
                    px: "20px",

                    "& .MuiButton-root": {
                        height: 32,
                        minHeight: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textTransform: "none",
                    },

                    "& .MuiAutocomplete-root": {
                        display: "flex",
                        alignItems: "center",
                    },

                    "& .MuiInputBase-root": {
                        height: 32,
                        minHeight: 32,
                        display: "flex",
                        alignItems: "center",
                        paddingTop: "0 !important",
                        paddingBottom: "0 !important",
                    },

                    "& .MuiAutocomplete-input": {
                        padding: "0 !important",
                    },

                    "& .MuiChip-root": {
                        height: 24,
                    },
                }}
            >
                {/* LEFT */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ height: "100%" }}
                >
                    <CustomTooltip title={"Show More KPIs"} arrow placement="top">
                        <IconButton
                            onClick={() => apiRef.current?.showPreferences("columns")}
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: "#919EAB",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <Iconify
                                icon="mingcute:calendar-week-line"
                                width={18}
                                height={18}
                            />
                        </IconButton>
                    </CustomTooltip>

                    <CustomTooltip title={"Apply Filters"} arrow placement="top">
                        <IconButton
                            onClick={() => apiRef.current?.showFilterPanel()}
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: "#919EAB",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <Iconify
                                icon="ic:round-filter-list"
                                width={18}
                                height={18}
                            />
                        </IconButton>
                    </CustomTooltip>

                    <CustomTooltip title={"Download Table"} arrow placement="top">
                        <IconButton
                            onClick={() =>
                                apiRef.current?.exportDataAsCsv({
                                    fileName: "exact_activations",
                                    utf8WithBom: true,
                                })
                            }
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: "#919EAB",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M14 10V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V10M4.66667 6.66667L8 10L11.3333 6.66667M8 10V2" stroke="#919EAB" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </IconButton>
                    </CustomTooltip>

                    {canImport && (
                        <CustomTooltip title={"Import KO Bidding Manual"} arrow placement="top">
                            <IconButton
                                onClick={onOpenCsvImport}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    padding: 0,
                                    color: '#919EAB',
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    }
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M14 8V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V8M5.33333 4.66667L8 2L10.6667 4.66667M8 2V10" stroke="#919EAB" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </IconButton>
                        </CustomTooltip>
                    )}
                </Stack>

                {/* RIGHT */}
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{ height: "100%" }}
                >
                    {selectionRows.length >= 1 && (

                        <Button
                            onClick={() =>
                                setOpenEnforceModal({
                                    open: true,
                                    row: selectionRows,
                                })
                            }
                            sx={{
                                backgroundColor:
                                    "rgba(145, 158, 171, 0.20)",
                                borderRadius: "8px",
                                px: 1.5,
                                mr: 1,
                            }}
                            size="small"
                        >
                            Enforce Max CPC ({selectionRows.length})
                        </Button>
                    )}

                    {selectionRows.length > 1 && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Button
                                onClick={() =>
                                    setModalDeactivateStrategy({
                                        open: true,
                                        row: selectionRows,
                                    })
                                }
                                sx={{
                                    backgroundColor:
                                        "rgba(145, 158, 171, 0.20)",
                                    borderRadius: "8px",
                                    px: 1.5,
                                }}
                                size="small"
                            >
                                Deactivate Bidding Strategy (
                                {selectionRows.length})
                            </Button>

                            <Button
                                onClick={() =>
                                    setModalScraping({
                                        open: true,
                                        row: selectionRows,
                                    })
                                }
                                size="small"
                                variant="contained"
                            >
                                Active Scraping ({selectionRows.length})
                            </Button>

                            <Button
                                onClick={() =>
                                    setModalCreateStrategy({
                                        open: true,
                                        row: selectionRows,
                                    })
                                }
                                size="small"
                                variant="contained"
                                sx={{ mr: 1 }}
                            >
                                Activate Bidding Strategy (
                                {selectionRows.length})
                            </Button>
                        </Box>
                    )}

                    <Box
                        display="flex"
                        alignItems="center"
                        position="relative"
                    >
                        {modifiedRows.length > 0 && (
                            <Box display={"flex"} flexDirection={"row"} gap={1} px={1}>
                                <Button
                                    onClick={() => {
                                        setModifiedRows([]);
                                        setLocalPatches({});
                                    }}
                                    size="small"
                                    variant="contained"
                                >
                                    Discard ({modifiedRows.length})
                                </Button>

                                <Button
                                    onClick={() => handleUpdateMaxCPC(modifiedRows)}
                                    size="small"
                                    variant="contained"
                                    sx={{
                                        minWidth: "unset",
                                        padding: "2px 6px !important",
                                        fontSize: "12px !important",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Apply ({modifiedRows.length})
                                </Button>
                            </Box>
                        )}

                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            pr={"20px"}
                        >
                            <Autocomplete
                                disablePortal
                                options={dateRangeOptions}
                                size="small"
                                disableClearable
                                onChange={(event, newValue) => {
                                    onDateRangeChange?.(newValue);
                                }}
                                getOptionLabel={(option) => option.label}
                                sx={{ width: 150 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment:
                                                selectedDateRange ? (
                                                    <Chip
                                                        label={
                                                            selectedDateRange.label
                                                        }
                                                        size="small"
                                                        sx={{
                                                            "&:hover": {
                                                                backgroundColor: "rgba(145, 158, 171, 0.50)",
                                                            },
                                                            backgroundColor:
                                                                "rgba(145, 158, 171, 0.32)",
                                                            color:
                                                                "rgba(51, 60, 71, 1)",
                                                        }}
                                                    />
                                                ) : null,
                                        }}
                                    />
                                )}
                            />

                            <Autocomplete
                                disablePortal
                                options={comparisonPeriodOptions}
                                disableClearable
                                size="small"
                                onChange={(event, newValue) => {
                                    onCompareDateChange?.(newValue);
                                }}
                                getOptionLabel={(option) => option.label}
                                sx={{ width: 160 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment:
                                                selectedCompareDate ? (
                                                    <Chip
                                                        label={
                                                            selectedCompareDate.label
                                                        }
                                                        size=
                                                        "small"
                                                        sx={{
                                                            "&:hover": {
                                                                backgroundColor: "rgba(145, 158, 171, 0.50)",
                                                            },
                                                            backgroundColor:
                                                                "rgba(145, 158, 171, 0.32)",
                                                            color:
                                                                "rgba(51, 60, 71, 1)",
                                                        }}
                                                    />
                                                ) : null,
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </Box>
                </Box>
            </GridToolbarContainer>
        );
    };

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => {
        const saved = localStorage.getItem("columnVisibilityModelActivationsExact");
        return saved ? JSON.parse(saved) : {
            campaign_ids: false,
            ad_group_names: false,
            ad_group_ids: false,
            keyword_id: false,
            region_name: false,
            avg_cpc: false,
            cost_last30d: false,
            platform: false,
        };
    });

    const handleColumnVisibilityChange = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
        localStorage.setItem("columnVisibilityModelActivationsExact", JSON.stringify(newModel));
    }, []);

    const [modifiedRows, setModifiedRows] = useState<{ id: string | number; max_cpc: number }[]>([]);

    const parseNumber = (value: any) => {
        if (value === "" || value == null) return null;

        const normalized = String(value).replace(",", ".");

        const n = Number(normalized);

        return isFinite(n) ? n : null;
    };

    const handleRowUpdate = (newRow: any, oldRow: any) => {
        const raw = newRow.max_cpc;

        const nextValue = parseNumber(raw);
        const prevValue = parseNumber(oldRow.max_cpc);

        if (nextValue === prevValue) {
            return oldRow;
        }

        const koManual = newRow.activations_details?.find(
            (d: any) => d.mode === "KO Bidding Manual" && d.status === true
        );

        const isInvalid =
            koManual && (nextValue === null || nextValue < 0.01);

        const updatedRow = {
            ...newRow,
            isEdited: true,
            isInvalid,
        };

        if (koManual) {
            setModifiedRows((prev: any) => {
                const filtered = prev.filter((r: any) => r.rowId !== newRow.id);

                if (!isInvalid) {
                    return [
                        ...filtered,
                        {
                            rowId: newRow.id,
                            id: koManual.id,
                            max_cpc: nextValue,
                        }
                    ];
                }

                return filtered;
            });
        }

        return updatedRow;
    };

    return (
        <Box
            sx={{
                height: '715px',
                width: '100%',
                "& .MuiDataGrid-columnHeader.keyword-header .MuiDataGrid-columnHeaderTitleContainer": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%!important",
                },
                "& .MuiDataGrid-columnHeader.keyword-header .MuiDataGrid-columnHeaderTitleContainerContent": {
                    display: "flex",
                    flex: 1,
                },
                "& .MuiDataGrid-columnHeader": {
                    backgroundColor: 'rgba(250, 251, 251)',
                    borderLeft: "1px solid #e9ecee"
                },
                "& .keyword-header-wrapper": {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                },
                position: "relative",
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                    outline: "none !important",
                },
                "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    borderLeft: "1px solid #e9ecee"
                },
                "& .MuiDataGrid-columnHeaders": {
                    borderBottom: "1px solid rgba(255,255,255,0.10)",
                },
                "& .MuiDataGrid-columnSeparator": {
                    color: "rgba(255,255,255,0.12)",
                    visibility: "visible",
                },

                "& .MuiDataGrid-cell:not(:last-of-type)": {
                    borderRight: "1px solid rgba(255,255,255,0.06)",
                },

                "& .css-ly985t-MuiDataGrid-root .MuiTablePagination-toolbar": {
                    color: '#919EAB!important'
                },
                "& .edited-row": {
                    backgroundColor: '#f1ebfb!important'
                },
                "& .MuiDataGrid-row.row-invalid": {
                    backgroundColor: "#ffe5e5 !important",
                },
                "& .MuiDataGrid-row:has(.Mui-error)": {
                    backgroundColor: "#ffe5e5 !important",
                },
                "& .MuiDataGrid-cell:has(.Mui-error) .MuiDataGrid-editInputCell input": {
                    backgroundColor: "#ffe5e5 !important",
                    border: '0px !important',
                },
                "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                    color: '#919EAB!important'
                },
                "& .css-nnlark-MuiSelect-select-MuiInputBase-input.css-nnlark-MuiSelect-select-MuiInputBase-input.css-nnlark-MuiSelect-select-MuiInputBase-input": {
                    borderRadius: '6px',
                    border: "1px solid rgba(145, 158, 171, 0.20)",
                    color: "#919EAB!important"
                },
                "& .MuiDataGrid-footerContainer .MuiTablePagination-actions .MuiIconButton-root": {
                    color: "#919EAB!important",
                },
                "& .MuiDataGrid-footerContainer .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
                    color: "#d0d5da!important",
                },
                "& .MuiDataGrid-cell:focus": {
                    outline: "none !important",
                },
                "& .MuiDataGrid-cell:focus-within": {
                    outline: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus": {
                    outline: "none !important",
                },
                ".css-ly985t-MuiDataGrid-root .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader, .css-ly985t-MuiDataGrid-root .MuiDataGrid-row--borderBottom .MuiDataGrid-filler, .css-ly985t-MuiDataGrid-root .MuiDataGrid-row--borderBottom .MuiDataGrid-scrollbarFiller": {
                    backgroundColor: '#fafbfb!important'
                }
            }}
        >
            <DataGridPro
                rowHeight={57}
                processRowUpdate={handleRowUpdate}
                apiRef={apiRef}
                getRowId={(r) => r.id}
                rows={mergedRows}
                rowCount={rowCount}
                paginationModel={{ page, pageSize }}
                pageSizeOptions={[10, 25, 50, 100, 200]}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                onPaginationModelChange={({ page, pageSize }) => {
                    onPageChange(page);
                    onPageSizeChange(pageSize);
                }}
                pagination
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                keepNonExistentRowsSelected
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                slots={{
                    toolbar: () => <CustomToolbar selectionRows={selectionRows} />,
                    filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
                }}
                checkboxSelection
                onRowSelectionModelChange={(newSelection: any) => {
                    const selectedIds = Array.isArray(newSelection) ? newSelection : [];
                    setSelectionRows(
                        mergedRows.filter((row) => selectedIds.includes(row.id))
                    );
                }}
                columnHeaderHeight={50}

                getRowClassName={(params) =>
                    [
                        params.row.isEdited ? "edited-row" : "",
                        params.row.isInvalid ? "row-invalid" : ""
                    ].join(" ")
                }
                isCellEditable={(params) => {
                    if (params.field !== "max_cpc") return false;

                    const activations = params.row.activations_details || [];
                    const koManual = activations.find(
                        (d: any) => d.status === true && d.mode === "KO Bidding Manual"
                    );

                    return Boolean(koManual);
                }}
                disableRowSelectionOnClick
                columns={columns}
                sx={{

                    "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #e9ecee",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        borderBottom: "1px solid rgba(0,0,0,0.1)",
                    },
                }}
            />

            <ModalScraping
                open={modalScraping.open}
                loading={loading}
                rowsToDisplay={rowsToDisplay}
                onClose={handleCloseScraping}
                onConfirm={handleConfirmScraping}
                setModalScraping={setModalScraping}
                setScrapingDetails={setScrapingDetails}
            />

            <ModalActivationsManual
                open={modalActivationManual.open && modalActivationManual.isActive}
                row={modalActivationManual.row}
                onClose={handleCloseActivationManual}
                setLocalPatches={setLocalPatches}
            />

            <ModalActivationsAuto
                open={modalActivationAuto.open && modalActivationAuto.isActive}
                row={modalActivationAuto.row}
                onClose={handleCloseActivationAuto}
                setLocalPatches={setLocalPatches}
            />

            <ModalCreateStrategy
                open={modalCreateStrategy.open}
                row={modalCreateStrategy.row}
                onClose={() => setModalCreateStrategy({ open: false, isActive: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            <ModalDeactivateStrategy
                refetch={refetch}
                open={modalDeactivateStrategy.open}
                row={modalDeactivateStrategy.row}
                onClose={() => setModalDeactivateStrategy({ open: false, isActive: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            <ConfirmDisableModal
                open={confirmDisable.open}
                row={confirmDisable.row}
                onClose={() => setConfirmDisable({ open: false, row: null, mode: null })}
                onConfirm={handleConfirmDisable}
            />

            <EnforceMaxCpcModal
                onFinish={handleFinish}
                open={openEnforceModal.open}
                onClose={() => setOpenEnforceModal({ open: false, row: null })}
                selectedRows={openEnforceModal.row}
            />
        </Box>
    );
}